<template>
    <div class="analysis-card">
        <div class="card border-0 shadow-sm mb-3" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
            <div class="card-body text-white">
                <div class="d-flex align-items-center">
                    <i class="fas fa-chart-line me-2"></i>
                    <h5 class="mb-0">{{ $t('adataPricePositioning.title') }}</h5>
                </div>
            </div>
        </div>
        <div class="analysis-card-body">
            <div class="row">
                <!-- SSD Analysis Section -->
                <div class="col-md-6">
                    <h4 class="text-center mb-3">{{ $t('adataPricePositioning.productType.ssd') }}</h4>
                    <div v-if="isLoading.SSD" class="loading-container">
                        <div class="loading-spinner"></div>
                        <p class="loading-text">{{ $t('adataPricePositioning.loading') }}</p>
                    </div>
                    <div v-else-if="error.SSD" class="alert alert-danger">
                        <i class="fas fa-exclamation-circle me-2"></i>{{ error.SSD }}
                    </div>
                    <div v-else>
                        <div class="chart-container" style="height: 250px;">
                            <canvas ref="ssdChart"></canvas>
                        </div>
                        <div class="legend-container mt-3">
                            <div v-for="(segment, index) in priceDistributionData.SSD.price_distribution" :key="index" class="legend-item">
                                <span class="legend-color-box" :style="{ backgroundColor: segmentColors[segment.segment] }"></span>
                                <span class="legend-text">{{ $t(`adataPricePositioning.segments.${segment.segment.toLowerCase()}`) }} {{ segment.percentage }}% ({{ segment.price_range }})</span>
                            </div>
                        </div>
                        <div class="recommendations-container mt-3">
                            <h3 class="recommendations-title"><i class="fas fa-lightbulb"></i>{{ $t('adataPricePositioning.brandInsight.title') }}</h3>
                            <p class="mb-0">{{ priceDistributionData.SSD.brand_insight }}</p>
                        </div>
                    </div>
                </div>

                <!-- DRAM Analysis Section -->
                <div class="col-md-6">
                    <h4 class="text-center mb-3">{{ $t('adataPricePositioning.productType.dram') }}</h4>
                    <div v-if="isLoading.DRAM" class="loading-container">
                        <div class="loading-spinner"></div>
                        <p class="loading-text">{{ $t('adataPricePositioning.loading') }}</p>
                    </div>
                    <div v-else-if="error.DRAM" class="alert alert-danger">
                        <i class="fas fa-exclamation-circle me-2"></i>{{ error.DRAM }}
                    </div>
                    <div v-else>
                        <div class="chart-container" style="height: 250px;">
                            <canvas ref="dramChart"></canvas>
                        </div>
                        <div class="legend-container mt-3">
                            <div v-for="(segment, index) in priceDistributionData.DRAM.price_distribution" :key="index" class="legend-item">
                                <span class="legend-color-box" :style="{ backgroundColor: segmentColors[segment.segment] }"></span>
                                <span class="legend-text">{{ $t(`adataPricePositioning.segments.${segment.segment.toLowerCase()}`) }} {{ segment.percentage }}% ({{ segment.price_range }})</span>
                            </div>
                        </div>
                        <div class="recommendations-container mt-3">
                            <h3 class="recommendations-title"><i class="fas fa-lightbulb"></i>{{ $t('adataPricePositioning.brandInsight.title') }}</h3>
                            <p class="mb-0">{{ priceDistributionData.DRAM.brand_insight }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import Chart from 'chart.js/auto';

const ssdChart = ref(null);
const dramChart = ref(null);
let ssdChartInstance = null;
let dramChartInstance = null;

const isLoading = ref({ SSD: false, DRAM: false });
const error = ref({ SSD: null, DRAM: null });

// Mock data for demonstration (will be replaced by API call)
const mockData = {
    SSD: {
        product_type: 'SSD',
        price_distribution: [
            { segment: 'Economy', percentage: 27.3, price_range: '≤$74' },
            { segment: 'Value', percentage: 18.2, price_range: '$74-$119' },
            { segment: 'Mid-range', percentage: 18.2, price_range: '$119-$163' },
            { segment: 'High-end', percentage: 36.4, price_range: '$163-$209' },
            { segment: 'Luxury', percentage: 0.0, price_range: '>$209' }
        ],
        brand_insight: 'ADATA is currently primarily positioned in the high-end market (36.4%), reflecting the brand\'s core value proposition.'
    },
    DRAM: {
        product_type: 'DRAM',
        price_distribution: [
            { segment: 'Economy', percentage: 30.0, price_range: '≤$50' },
            { segment: 'Value', percentage: 25.0, price_range: '$50-$100' },
            { segment: 'Mid-range', percentage: 20.0, price_range: '$100-$150' },
            { segment: 'High-end', percentage: 15.0, price_range: '$150-$200' },
            { segment: 'Luxury', percentage: 10.0, price_range: '>$200' }
        ],
        brand_insight: 'ADATA DRAM products show a balanced distribution across price segments, with a notable presence in the economy and value categories.'
    }
};

const segmentColors = {
    'Economy': '#FF6384', // Red
    'Value': '#36A2EB',   // Blue
    'Mid-range': '#FFCE56', // Yellow
    'High-end': '#4BC0C0',  // Cyan
    'Luxury': '#9966FF'    // Purple
};

const priceDistributionData = ref({ SSD: {}, DRAM: {} });

const fetchData = async (productType) => {
    isLoading.value[productType] = true;
    error.value[productType] = null;
    try {
        // Simulate API call
        const data = await new Promise((resolve, reject) => {
            setTimeout(() => {
                if (mockData[productType]) {
                    resolve(mockData[productType]);
                } else {
                    reject(new Error('Data not found for this product type.'));
                }
            }, 500);
        });
        priceDistributionData.value[productType] = data;
    } catch (err) {
        error.value[productType] = err.message || 'Failed to fetch data.';
        priceDistributionData.value[productType] = {}; // Clear data on error
    } finally {
        isLoading.value[productType] = false;
    }
};

const renderChart = (chartRef, chartInstanceRef, data) => {
    if (chartInstanceRef.value) {
        chartInstanceRef.value.destroy();
    }

    if (!chartRef.value || !data || !data.price_distribution) {
        return; // Don't render if chart element or data is not ready
    }

    const ctx = chartRef.value.getContext('2d');

    chartInstanceRef.value = new Chart(ctx, {
        type: 'doughnut',
        data: {
            labels: data.price_distribution.map(d => d.segment),
            datasets: [{
                data: data.price_distribution.map(d => d.percentage),
                backgroundColor: data.price_distribution.map(d => segmentColors[d.segment]),
                hoverOffset: 4
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false // We will create a custom legend
                },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            const label = context.label || '';
                            const value = context.raw || 0;
                            const priceRange = data.price_distribution[context.dataIndex].price_range;
                            return `${label}: ${value}% (${priceRange})`;
                        }
                    }
                }
            }
        }
    });
};

// Fetch data for both SSD and DRAM on component mount
(async () => {
    await fetchData('SSD');
    if (!error.value.SSD) {
        renderChart(ssdChart, ref(ssdChartInstance), priceDistributionData.value.SSD);
    }

    await fetchData('DRAM');
    if (!error.value.DRAM) {
        renderChart(dramChart, ref(dramChartInstance), priceDistributionData.value.DRAM);
    }
})();

</script>

<style scoped>
/* Reusing styles from IntelligenceAnalysisView.vue for consistency */
.analysis-card {
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    display: flex;
    flex-direction: column;
}

.analysis-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.analysis-card-header {
    padding: 25px;
    display: flex;
    align-items: center;
    position: relative;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
}

.analysis-card-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    color: #ffffff;
    margin-right: 20px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    flex-shrink: 0;
}

.analysis-card-title {
    font-size: 1.4rem;
    font-weight: 600;
    color: #343a40;
    margin-bottom: 5px;
}

.analysis-card-description {
    font-size: 0.95rem;
    color: #6c757d;
    margin-bottom: 0;
}

.analysis-card-body {
    padding: 25px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
}

.recommendations-container {
    background-color: #e6f7ff;
    border: 1px solid #b3e0ff;
    border-radius: 10px;
    padding: 20px;
    margin-top: 20px;
    opacity: 1;
    transform: translateY(0);
    transition: opacity 0.5s ease, transform 0.5s ease;
}

.recommendations-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #0056b3;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
}

.recommendations-title i {
    color: #007bff;
    margin-right: 10px;
}

.legend-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
}

.legend-item {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    color: #343a40;
}

.legend-color-box {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    margin-right: 8px;
    flex-shrink: 0;
}

.legend-text {
    white-space: nowrap;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px 0;
    color: #6c757d;
}

.loading-spinner {
    border: 4px solid rgba(0, 123, 255, 0.1);
    border-top: 4px solid #007bff;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin-bottom: 15px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-text {
    font-size: 1.1rem;
    font-weight: 500;
}

.alert {
    border-radius: 8px;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
}

.alert i {
    margin-right: 10px;
}

/* Variables for consistent theming */
:root {
    --primary-color: #007bff;
    --secondary-color: #6c757d;
    --success-color: #28a745;
    --info-color: #17a2b8;
    --warning-color: #ffc107;
    --danger-color: #dc3545;
}
</style>